'use strict';

const enumFromObject = require('./enumfromobject');

/**
 * Defines alarm audio volumes (used for both hubs and areas)
 *
 * @enum {String}
 * @readonly
 */
const AlarmAudioVolume = {
  OFF: 'OFF',
  LOW: 'LOW',
  MID: 'MID',
  HIGH: 'HIGH',
};

/**
 * Defines alarm siren volumes
 *
 * @enum {String}
 * @readonly
 */
const AlarmSirenVolume = {
  LOW: 'LOW',
  MID: 'MID',
  HIGH: 'HIGH',
};

/**
 * Defines alarm hub keypad disarm modes
 *
 * @enum {String}
 * @readonly
 */
const AlarmKeypadDisarmMode = {
  DISARM_ANYTIME: 'DISARM_ANYTIME',
  DISARM_ENTRY_TIME: 'DISARM_ENTRY_TIME',
};

/**
 * Defines alarm hub keyfob disarm modes
 *
 * @enum {String}
 * @readonly
 */
const AlarmKeyfobDisarmMode = {
  DISARM_ANYTIME: 'DISARM_ANYTIME',
  DISARM_ENTRY_TIME: 'DISARM_ENTRY_TIME',
  DISARM_FORBIDDEN: 'DISARM_FORBIDDEN',
};

/**
 * Defines alarm battery status
 *
 * @enum {String}
 * @readonly
 */
const AlarmHubBatteryStatus = {
  OK: 'OK',
  LOW: 'LOW',
  FAULT: 'FAULT',
  DISCONNECT: 'DISCONNECT',
};

/**
 * Defines alarm hub ethernet statuses
 *
 * @enum {String}
 * @readonly
 */
const AlarmEthernetStatus = {
  ETHERNET_STATUS_DISABLED: 'ETHERNET_STATUS_DISABLED',
  ETHERNET_STATUS_UNPLUGGED: 'ETHERNET_STATUS_UNPLUGGED',
  ETHERNET_STATUS_10BASET_HALF_DUPLEX: 'ETHERNET_STATUS_10BASET_HALF_DUPLEX',
  ETHERNET_STATUS_10BASET_FULL_DUPLEX: 'ETHERNET_STATUS_10BASET_FULL_DUPLEX',
  ETHERNET_STATUS_100BASET_HALF_DUPLEX: 'ETHERNET_STATUS_100BASET_HALF_DUPLEX',
  ETHERNET_STATUS_100BASET_FULL_DUPLEX: 'ETHERNET_STATUS_100BASET_FULL_DUPLEX',
};

/**
 * Defines alarm hub status WiFi statuses
 *
 * @enum {String}
 * @readonly
 */
const AlarmWifiStatus = {
  WIFI_STATUS_DISABLED: 'WIFI_STATUS_DISABLED',
  WIFI_STATUS_CONNECTED: 'WIFI_STATUS_CONNECTED',
  WIFI_STATUS_CONNECTING: 'WIFI_STATUS_CONNECTING',
};

/**
 * Defines alarm hub secure communication statuses
 *
 * @enum {String}
 * @readonly
 */
const AlarmSecureComStatus = {
  SECURECOM_CONNECTED: 'SECURECOM_CONNECTED',
  SECURECOM_NO_NETWORK_ACCESS: 'SECURECOM_NO_NETWORK_ACCESS',
  SECURECOM_UNABLE_TO_RESOLVE_SERVER_ADDRESS: 'SECURECOM_UNABLE_TO_RESOLVE_SERVER_ADDRESS',
  SECURECOM_NO_ANSWER_FROM_SERVER: 'SECURECOM_NO_ANSWER_FROM_SERVER',
  SECURECOM_CONNECTING: 'SECURECOM_CONNECTING',
  SECURECOM_NOT_CONNECTED: 'SECURECOM_NOT_CONNECTED',
};

/**
 * Defines alarm hub arm states
 *
 * @enum {String}
 * @readonly
 */
const AlarmArmState = {
  ARM_STATE_UNSET: 'ARM_STATE_UNSET',
  ARM_STATE_PART_ARM: 'ARM_STATE_PART_ARM',
  ARM_STATE_ARMED: 'ARM_STATE_ARMED',
};

/**
 * Defines alarm arm states for areas
 * This is :state parameter in the API endpoint PUT /alarms/:alarmID/state/:state
 *
 * @enum {String}
 * @readonly
 */
const AlarmAreaArmState = {
  FULL_ARM: 'FULL_ARM',
  PARTIAL_ARM: 'PARTIAL_ARM',
  DISARM: 'DISARM',
};

/**
 * Arm areas
 */
const AlarmArmArea = {
  AREA_1: 'AREA_1',
  AREA_2: 'AREA_2',
  AREA_3: 'AREA_3',
  AREA_4: 'AREA_4',
  ALL_AREAS: 'ALL_AREAS',
};

/**
 * pubnub events regarding alarms
 */
const AlarmEvents = {
  HUB_ONLINE: 'HUB_ONLINE',
  HUB_OFFLINE: 'HUB_OFFLINE',
  NEW_DEVICE: 'NEW_DEVICE',
  DEVICE_ACTIVATED: 'DEVICE_ACTIVATED',
  DEVICE_UPDATED: 'DEVICE_UPDATED',
  UPDATE_PROCESS_COMPLETED: 'UPDATE_PROCESS_COMPLETED',
  UPDATE_ABORTED_BY_HUB: 'UPDATE_ABORTED_BY_HUB',
  ALARM: 'ALARM',
  BATTERY_LOW: 'BATTERY_LOW',
  ALARM_INVALID_CODE_TAMPER: 'ALARM_INVALID_CODE_TAMPER',
  ALARM_HUB_TAMPER: 'ALARM_HUB_TAMPER',
  ALARM_DEVICE_TAMPER: 'ALARM_DEVICE_TAMPER',
  STATUS_MESSAGES: 'STATUS_MESSAGES',
};

/**
 * Defines events that can trigger an alarm.
 *
 * @enum {String}
 * @readonly
 */
const AlarmTriggerEvent = {
  FIRE: 'FIRE', // Fire alarm, sent when Smoke detector activates
  PANIC: 'PANIC', // Panic alarm, sent when Panic button is pressed or Panic combination keys are pressed on Keypad or Keyfob
  SILENT_PANIC: 'SILENT_PANIC', // Silent Panic alarm, sent when panic button is pressed on Panic Buttons with the Silent Alarm option enabled
  ALARM_DEVICE: 'ALARM_DEVICE', // Alarm, sent when a device triggers the alarm, and the sirens activates
  REMOTE: 'REMOTE', // Remote triggered alarm (Alarm was activated by API)
  TAMPER: 'TAMPER', // Tamper trigger alarm
};

/**
 * Defines events regarding push notifications.
 *
 * @enum {String}
 * @readonly
 */
const AlarmActions = {
  ALARM_CONTACT_OPENED: 'alarm_contact_opened',
  ALARM_CONTACT_CLOSED: 'alarm_contact_closed',
  ALARM_HUB_ONLINE: 'alarm_hub_online',
  ALARM_HUB_OFFLINE: 'alarm_hub_offline',
  ALARM_DEVICE_OFFLINE: 'alarm_device_offline',
  ALARM_DEVICE_ONLINE: 'alarm_device_online',
  ALARM_HUB_BATTERY_LOW: 'alarm_hub_battery_low',
  ALARM_DEVICE_BATTERY_LOW: 'alarm_device_battery_low',
  ALARM_HUB_TAMPER: 'alarm_hub_tamper',
  ALARM_HUB_TAMPER_RESOLVED: 'alarm_hub_tamper_resolved',
  ALARM_DEVICE_TAMPER: 'alarm_device_tamper',
  ALARM_DEVICE_TAMPER_RESOLVED: 'alarm_device_tamper_resolved',
  ALARM_DEVICE_REMOVED: 'alarm_device_removed',
  ALARM_DEVICE_ADDED: 'alarm_device_added',
  ALARM_DEVICE_UPDATED: 'alarm_device_updated',
  ALARM_ARM_STATE_UPDATED: 'alarm_arm_state_updated',
  ALARM_INVALID_CODE_TAMPER: 'alarm_invalid_code_tamper',
  ALARM_AREA_ADDED: 'alarm_area_added',
  ALARM_AREA_REMOVED: 'alarm_area_removed',
  ALARM_CODE_CHANGED: 'alarm_code_changed',
  FIRE: 'alarm_fire_triggered',
  PANIC: 'alarm_panic_triggered',
  ALARM_DEVICE: 'alarm_device_triggered',
  REMOTE: 'alarm_remote_panic_triggered',
};

/**
 * Defines alarm arm modes
 *
 * @enum {String}
 * @readonly
 */
const AlarmArmMode = {
  OFF: 'OFF',
  PERIMETER: 'PERIMETER',
  ENTRY_DELAY: 'ENTRY_DELAY',
  ALARM: 'ALARM',
};

/**
 * Defines alarm device sensitivity
 *
 * @enum {String}
 * @readonly
 */
const AlarmSensitivity = {
  LOW: 'LOW',
  MID: 'MID',
  HIGH: 'HIGH',
};

/**
 * Defines alarm device connection quality levels
 */
const AlarmDeviceConnectionQuality = {
  GOOD: 'GOOD',
  POOR: 'POOR',
  UNKNOWN: 'UNKNOWN',
};

/*
 * Defines alarm connection type
 *
 * @enum {String}
 * @readonly
 */
const AlarmConnectionType = {
  ETHERNET: 'ETHERNET',
  WIFI: 'WIFI',
  CELLULAR: 'CELLULAR',
};

module.exports = {
  AlarmAudioVolume: enumFromObject(AlarmAudioVolume),
  AlarmKeypadDisarmMode: enumFromObject(AlarmKeypadDisarmMode),
  AlarmKeyfobDisarmMode: enumFromObject(AlarmKeyfobDisarmMode),
  AlarmHubBatteryStatus: enumFromObject(AlarmHubBatteryStatus),
  AlarmEthernetStatus: enumFromObject(AlarmEthernetStatus),
  AlarmWifiStatus: enumFromObject(AlarmWifiStatus),
  AlarmSecureComStatus: enumFromObject(AlarmSecureComStatus),
  AlarmArmState: enumFromObject(AlarmArmState),
  AlarmArmArea: enumFromObject(AlarmArmArea),
  AlarmEvents: enumFromObject(AlarmEvents),
  AlarmArmMode: enumFromObject(AlarmArmMode),
  AlarmSensitivity: enumFromObject(AlarmSensitivity),
  AlarmSirenVolume: enumFromObject(AlarmSirenVolume),
  AlarmDeviceConnectionQuality: enumFromObject(AlarmDeviceConnectionQuality),
  AlarmConnectionType: enumFromObject(AlarmConnectionType),
  AlarmTriggerEvent: enumFromObject(AlarmTriggerEvent),
  AlarmActions: enumFromObject(AlarmActions),
  AlarmAreaArmState: enumFromObject(AlarmAreaArmState),
};
